import React, { useState, useEffect } from 'react';
import {
	Avatar,
	Box,
	Tabs,
	Tab,
	Typography,
	Switch,
	FormControlLabel,
	Button,
	MenuItem, Select, InputLabel, FormControl, OutlinedInput, Chip,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { AddCircleOutlineOutlined } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@mui/styles';

import EditableElement from './../../common/components/CharacterSheet/EditableElement';
import { AvatarSelectionDialog } from './../../components';
import {
	createCharacter,
	getCharacter,
	getCharacterTemplate,
	updateCharacter,
} from './../../common/services/characterRequests';
import { getUserAvatars } from './../../common/services/avatarsRequests';
import { USERS } from './../../common/constants';
import { getAwsUserId } from '../../common/services/awsUserRequest';


const useStyles = makeStyles((theme) => ({
	header: {
		borderBottom: `1px solid ${theme.palette.divider}`,
		display: 'flex',
		alignItems: 'center',
		padding: theme.spacing(2),
	},
	userAvatar: {
		marginRight: theme.spacing(2),
		width: '40px', // Set your desired width
		height: '40px', // Set your desired height
		borderRadius: '50%',
		backgroundColor: theme.palette.primary.main,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: theme.palette.primary.contrastText,
	},
	headerText: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2),
	},
	subHeader: {
		display: 'flex',
		justifyContent: 'space-evenly',
		alignItems: 'center',
	},
	tabContainer: {
		maxWidth: '1000px', // Adjust the width as needed
		margin: '0 auto',
	},
	tabContent: {
		display: 'grid',
		gap: theme.spacing(3),
		paddingTop: theme.spacing(4),
		flexWrap: 'wrap',
		justifyContent: 'center',
	},
	avatarContainer: {
		margin: theme.spacing(2),
	},
	avatar: {
		// Adjust the styles as needed
		backgroundColor: theme.palette.secondary.main,
		width: theme.spacing(7),
		height: theme.spacing(7),
		margin: theme.spacing(1),
		cursor: 'pointer',
	},
	selectedAvatar: {
		border: `3px solid ${theme.palette.primary.contrastText}`,
	},
}));

const Character = ({ editable = true }) => {
	const classes = useStyles();
	const { templateId, characterId } = useParams();
	const [template, setTemplate] = useState(null);
	const [character, setCharacter] = useState(null);
	const [avatar, setAvatar] = useState(null);
	const [activeTab, setActiveTab] = useState(0);
	const [isSaving, setIsSaving] = useState(false);
	const [avatarDialogOpen, setAvatarDialogOpen] = useState(false); // State for the avatar dialog
	const [currentUserId, setMyUserId] = useState(null);
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();


	useEffect(() => {
		const fetchData = async () => {
			try {
				const userId = await getAwsUserId();
				setMyUserId(userId);
				if (templateId) {
					const templateResponse = await getCharacterTemplate(templateId);
					setTemplate(templateResponse);
					// Create a basic character from the template
					const basicCharacter = {
						name: '',
						template: templateResponse.template,
						owner: currentUserId,
						masters: [currentUserId],
						isPublic: false,
						avatarId: '',
						sections: templateResponse.sections.map((section) => ({
							id: section.id,
							name: section.name,
							type: section.type,
							elements: section.elements.map((element) => {
								const defaultValue =
									section.type === 'counter' && element.value != null ? element.value : '';

								return {
									...element,
									value: defaultValue,
								};
							}),
						})),
					};
					setCharacter(basicCharacter);
				}
				if (characterId) {
					const characterResponse = await getCharacter(characterId);
					setCharacter(characterResponse);
					if (characterResponse.avatarId) {
						const avatarsResponse = await getUserAvatars();
						const currentAvatar = avatarsResponse.avatars.find(
							(x) => x.ID == characterResponse.avatarId
						);
						if (currentAvatar) {
							setAvatar(currentAvatar);
						}
					}
				}
			} catch (error) {
				console.error('Error fetching data:', error);
			}
		};

		fetchData();
	}, [templateId, characterId]);

	const handleElementChange = (sectionId, elementId, newValue) => {
		if (character) {
			const updatedCharacter = { ...character };
			const sectionIndex = updatedCharacter.sections.findIndex(
				(section) => section.id === sectionId
			);
			if (sectionIndex !== -1) {
				const elementIndex = updatedCharacter.sections[sectionIndex].elements.findIndex(
					(element) => element.id === elementId
				);
				if (elementIndex !== -1) {
					updatedCharacter.sections[sectionIndex].elements[elementIndex].value = newValue;
					setCharacter(updatedCharacter);
				}
			}
		}
	};

	const handleSave = async () => {
		if (character) {
			setIsSaving(true);
			if (!character.owner) {
				enqueueSnackbar('Owner cannot be empty.', { variant: 'error' });
				setIsSaving(false);
				return;
			}
			const params = {
				name: character.name,
				template: template ? template : character.template,
				owner: character.owner,
				masters: character.masters.filter((master) => master !== null),
				isPublic: character.isPublic,
				avatarId: character.avatarId,
				sections: character.sections,
			};

			try {
				if (characterId) {
					console.log('update character params', params);
					await updateCharacter(characterId, params);
					// Update existing character
					// Call the update_character API with params
				} else {
					// Create new character
					console.log('create character params', params);
					await createCharacter(params);
					// Call the create_character API with params
					enqueueSnackbar('Character created successfully!', { variant: 'success' });
				}

				// Handle success and update UI if needed
			} catch (error) {
				console.error('Error saving character:', error);
			} finally {
				setIsSaving(false);
			}
		}
	};

	const handleAvatarClick = () => {
		// Open the avatar selection dialog
		setAvatarDialogOpen(true);
	};

	const handleAvatarSelect = (avatar) => {
		if (character) {
			const updatedCharacter = { ...character, avatarId: avatar.ID };
			setCharacter(updatedCharacter);
			setAvatar(avatar);
		}
		setAvatarDialogOpen(false);
	};

	if (!template && !character) {
		return <div>Loading...</div>;
	}

	const formatDate = (date) => {
		return new Date(date).toLocaleString('es-ES', {
			day: 'numeric',
			month: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: 'numeric',
		});
	};

	const sections = template ? template.sections : character.sections;

	return (
		<Box>
			{/* Header */}
			<Box className={classes.header}>
				{/* User Avatar */}

				<Box className={classes.userAvatar} onClick={handleAvatarClick}>
					{avatar ? (
						<Avatar className={classes.avatar} src={avatar.url} />
					) : (
						<AddCircleOutlineOutlined className={classes.avatarIcon} />
					)}
				</Box>

				{/* User Name */}
				<Box className={classes.headerText}>
					<EditableElement
						name="User Name"
						value={character ? character.name : ''}
						type="text"
						editable={editable}
						onChange={(newValue) => {
							if (character) {
								const updatedCharacter = { ...character, name: newValue };
								setCharacter(updatedCharacter);
							}
						}}
					/>
					{/* Owner Selector */}
					{editable && (
						<FormControl fullWidth>
							<InputLabel id="owner-select-label">Owner</InputLabel>
							<Select
								labelId="owner-select-label"
								value={character?.owner || ''}
								onChange={(e) => {
									const newOwner = e.target.value;
									const updatedCharacter = { ...character, owner: newOwner };

									// Add current user to masters if not owner
									if (newOwner !== currentUserId && !updatedCharacter.masters.includes(currentUserId)) {
										updatedCharacter.masters = [...updatedCharacter.masters, currentUserId];
									}

									setCharacter(updatedCharacter);
								}}
								input={<OutlinedInput label="Owner" />}
							>
								{USERS.map((user) => {
									const email = user.Attributes.find((att) => att.Name === 'email')?.Value;
									return (
										<MenuItem key={user.Username} value={user.Username}>
											{email || user.Username}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>
					)}

					{/* Masters Selector */}
					{editable && (
						<FormControl fullWidth>
							<InputLabel id="masters-select-label">Masters</InputLabel>
							<Select
								labelId="masters-select-label"
								multiple
								value={character?.masters || []}
								onChange={(e) => {
									const newMasters = e.target.value;
									setCharacter((prev) => ({
										...prev,
										masters: newMasters,
									}));
								}}
								input={<OutlinedInput label="Masters" />}
								renderValue={(selected) => (
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
										{selected.map((value) => {
											const user = USERS.find((u) => u.Username === value);
											const email = user?.Attributes.find((att) => att.Name === 'email')?.Value;
											return <Chip key={value} label={email || value} />;
										})}
									</Box>
								)}
							>
								{USERS.map((user) => {
									const email = user.Attributes.find((att) => att.Name === 'email')?.Value;
									return (
										<MenuItem key={user.Username} value={user.Username}>
											{email || user.Username}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>
					)}

				</Box>
			</Box>

			{/* Template Name and Switch */}
			<Box className={classes.subHeader} p={2}>
				<Typography variant="h6">{template ? template.name : ''}</Typography>
				{character && character.creationDate && (
					<Typography variant="caption">
						Created: {formatDate(character.creationDate)}
					</Typography>
				)}
				{character && character.modifiedDate && (
					<Typography variant="caption">
						Modified: {formatDate(character.modifiedDate)}
					</Typography>
				)}

				<FormControlLabel
					control={
						<Switch
							checked={character ? character.isPublic : false}
							onChange={(event) => {
								if (character) {
									const updatedCharacter = {
										...character,
										isPublic: event.target.checked,
									};
									setCharacter(updatedCharacter);
								}
							}}
						/>
					}
					label="Public"
				/>
			</Box>

			{/* Tabs */}
			<Box className={classes.tabContainer}>
				<Tabs value={activeTab} onChange={(event, newValue) => setActiveTab(newValue)}>
					{sections?.map((section, index) => (
						<Tab key={section.id} label={section.name} />
					))}
				</Tabs>
			</Box>

			{/* Tab Content */}
			<Box>
				{sections?.map((section, index) => (
					<Box
						className={classes.tabContent}
						key={section.id}
						display={activeTab === index ? 'flex!important' : 'none!important'}
					// Removed className={classes.tabContainer}
					>
						{section?.elements?.map((element) => (
							<Box key={element.id} mb={2}>
								<EditableElement
									name={element.label}
									value={element.value}
									type={section.type}
									editable={editable}
									onChange={(newValue) =>
										handleElementChange(section.id, element.id, newValue)
									}
								/>
							</Box>
						))}
					</Box>
				))}
			</Box>

			{/* Save Button */}
			<Box mt={3} textAlign="center">
				<Button
					variant="contained"
					color="primary"
					disabled={isSaving}
					onClick={handleSave}>
					{isSaving ? 'Saving...' : 'Save'}
				</Button>
			</Box>
			<AvatarSelectionDialog
				open={avatarDialogOpen}
				onClose={() => setAvatarDialogOpen(false)}
				onSelect={handleAvatarSelect}
				selectedAvatarId={character ? character.avatarId : null}
			/>
		</Box>
	);
};

export default Character;
